import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { getStory } from "../lib/storiesData";

const Header = () => (
  <header className="fixed top-0 inset-x-0 z-50 bg-gradient-to-b from-black/60 to-transparent">
    <div className="mx-auto max-w-[1500px] w-full px-5 sm:px-8 h-16 flex items-center justify-between">
      <Link to="/" data-testid="site-logo" className="shrink-0">
        <img
          src="/owos-logo-badge.png"
          alt="One World of Sports"
          className="h-11 w-auto object-contain"
        />
      </Link>
      <Link
        to="/stories"
        data-testid="story-back-stories-top"
        className="group inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] font-bold text-white/80 hover:text-white transition-colors"
      >
        <ArrowLeft className="h-4 w-4 group-hover:-translate-x-0.5 transition-transform" />
        Stories
      </Link>
    </div>
  </header>
);

const FadeIn = ({ children, className = "", delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 26 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-70px" }}
    transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay }}
    className={className}
  >
    {children}
  </motion.div>
);

export default function Story() {
  const { slug } = useParams();
  const story = getStory(slug);

  useEffect(() => {
    document.title = story
      ? `${story.title} — Stories — One World of Sports`
      : "Stories — One World of Sports";
  }, [story]);

  if (!story) {
    return (
      <div className="bg-[#0a0a0a] min-h-screen text-white flex flex-col items-center justify-center gap-6 px-6">
        <p className="font-display uppercase text-4xl">Story not found</p>
        <Link
          to="/stories"
          className="text-xs uppercase tracking-[0.28em] font-bold text-neutral-400 hover:text-white"
        >
          Back to Stories
        </Link>
      </div>
    );
  }

  const { problem, connection, resolution } = story;

  return (
    <div data-testid="story-page" className="bg-[#0a0a0a] min-h-screen text-white">
      <Header />

      {/* 1. THE IMAGE — the story begins with the photograph */}
      <section data-testid="story-hero" className="relative h-[92svh] w-full overflow-hidden">
        <img
          src={story.hero}
          alt={`Wingsuit flyers with smoke trails above the Taj Mahal complex — ${story.locationDate}`}
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-transparent to-transparent" />
        <div className="absolute bottom-0 inset-x-0 p-5 sm:p-8">
          <div className="mx-auto max-w-[1500px] w-full flex items-end justify-between gap-4">
            <span className="sr-only">Story {story.num} — {story.title}</span>
            <p
              data-testid="story-location-date"
              className="text-xs sm:text-sm uppercase tracking-[0.32em] font-bold text-white/90"
            >
              {story.locationDate}
            </p>
            <span className="font-display text-2xl sm:text-3xl text-white/40">{story.num}</span>
          </div>
        </div>
      </section>

      {/* 2. THE PROBLEM */}
      <section
        data-testid="story-problem"
        className="mx-auto max-w-[1500px] px-5 sm:px-8 pt-20 sm:pt-32 pb-16 sm:pb-24"
      >
        <FadeIn>
          <p className="text-xs uppercase tracking-[0.3em] font-bold text-neutral-500">
            {problem.dateLabel}
          </p>
          <h2 className="mt-5 max-w-4xl font-display uppercase leading-[0.95] text-white text-4xl sm:text-6xl lg:text-7xl">
            {problem.headline}
          </h2>
        </FadeIn>

        <FadeIn className="mt-12 sm:mt-16" delay={0.05}>
          <figure className="overflow-hidden">
            <img
              src={story.detail}
              alt="Two wingsuit flyers trailing green and orange smoke, one wearing a Red Bull / Soul Flyers suit"
              className="w-full h-auto object-contain"
            />
          </figure>
        </FadeIn>

        <FadeIn className="mt-12 sm:mt-16 max-w-2xl ml-auto" delay={0.05}>
          <div className="space-y-5 text-lg sm:text-xl leading-relaxed text-neutral-300">
            {problem.paragraphs.map((p, i) => (
              <p key={i} data-testid={`problem-para-${i}`}>
                {p}
              </p>
            ))}
          </div>

          <blockquote
            data-testid="story-quote"
            className="mt-12 border-l-2 border-[#e62429] pl-6"
          >
            <p className="font-display uppercase text-3xl sm:text-4xl leading-tight text-white">
              “{problem.quote}”
            </p>
            <cite className="mt-4 block not-italic text-xs uppercase tracking-[0.28em] font-bold text-neutral-500">
              {problem.quoteAttribution}
            </cite>
          </blockquote>
        </FadeIn>
      </section>

      {/* 3. THE CONNECTION */}
      <section
        data-testid="story-connection"
        className="border-t border-white/10 bg-[#0c0c0c]"
      >
        <div className="mx-auto max-w-[1500px] px-5 sm:px-8 py-20 sm:py-32">
          <FadeIn>
            <h2 className="max-w-4xl font-display uppercase leading-[0.95] text-white text-4xl sm:text-6xl lg:text-7xl">
              {connection.headline}
            </h2>
          </FadeIn>

          <FadeIn className="mt-12 max-w-2xl" delay={0.05}>
            <div className="space-y-5 text-lg sm:text-xl leading-relaxed text-neutral-300">
              {connection.paragraphs.map((p, i) => (
                <p key={i} data-testid={`connection-para-${i}`}>
                  {p}
                </p>
              ))}
            </div>
          </FadeIn>

          <FadeIn className="mt-16 sm:mt-20 max-w-3xl" delay={0.05}>
            <div className="space-y-2 text-neutral-400 text-xl sm:text-2xl leading-snug">
              {connection.closing.map((line, i) => (
                <p key={i} data-testid={`connection-closing-${i}`}>
                  {line}
                </p>
              ))}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Resolution */}
      <section
        data-testid="story-resolution"
        className="mx-auto max-w-[1500px] px-5 sm:px-8 py-24 sm:py-40"
      >
        <FadeIn>
          <div className="space-y-1">
            {resolution.lines.map((line, i) => (
              <p
                key={i}
                data-testid={`resolution-line-${i}`}
                className="font-display uppercase leading-[0.95] text-white text-5xl sm:text-7xl lg:text-8xl"
              >
                {line}
              </p>
            ))}
          </div>
          <p className="mt-8 text-xs uppercase tracking-[0.3em] font-bold text-neutral-500">
            {resolution.dateLabel}
          </p>
        </FadeIn>
      </section>

      {/* Routes back */}
      <section className="border-t border-white/10 py-16">
        <div className="mx-auto max-w-[1500px] px-5 sm:px-8 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6">
          <Link
            to="/stories"
            data-testid="story-back-stories-bottom"
            className="group inline-flex items-center gap-3 border border-white/20 px-8 py-4 text-xs uppercase tracking-[0.28em] font-bold text-white hover:bg-white hover:text-black transition-colors"
          >
            <ArrowLeft className="h-4 w-4 group-hover:-translate-x-0.5 transition-transform" />
            Back to Stories
          </Link>
          <Link
            to="/#gateways"
            data-testid="story-back-explore-bottom"
            className="group inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em] font-bold text-neutral-400 hover:text-white transition-colors"
          >
            Back to Explore
            <ArrowUpRight className="h-4 w-4 opacity-40 group-hover:opacity-100 transition-opacity" />
          </Link>
        </div>
      </section>
    </div>
  );
}
